// packages/Shell/Pandora/services/personalization-item.ts

import type { PersonalizationSettingType } from './mx-personalization-panel-service';

export interface PersonalizationDrilldownProperty {
	id: string;
	icon: string;
	label: string;
	type?: 'readonly' | 'toggle' | 'select' | 'number';
	value?: boolean | string | number;
	options?: Array<{ value: boolean | string | number; label: string }>;
	valueSummary?: string;
	onChange?: (value: boolean | string | number) => Promise<void> | void;
	onReset?: () => Promise<void>;
}

export interface PersonalizationDrilldownCard {
	id: string;
	label: string;
	type?: 'toggle';
	value?: boolean;
	defaultValue?: boolean;
	onChange?: (value: boolean) => Promise<void> | void;
	onOpen?: () => void;
	onReset?: () => Promise<void>;
	properties: PersonalizationDrilldownProperty[];
}

/**
 * Result of a `childrenLoader` call: the cards for the requested page/slice, plus the
 * total number of cards available (across all pages) so the panel can show an accurate
 * count badge and know whether a "load more" affordance is needed.
 */
export interface PersonalizationDrilldownPage {
	cards: PersonalizationDrilldownCard[];
	total: number;
}

export type PersonalizationValueType = 'boolean' | 'string' | 'number';
export type PersonalizationInputType = 'toggle' | 'radio' | 'select' | 'text' | 'number';

export interface PersonalizationItem {
	key: string;
	title: string;
	category?: string;
	/**
	 * Required for leaf settings; ignored (and safely omittable) for drilldown container
	 * items — a container's own value is never rendered or stored.
	 */
	valueType?: PersonalizationValueType;
	/**
	 * Optional UI control override. If omitted, inferred from valueType:
	 *   boolean → toggle
	 *   string + options → select, string (no options) → text
	 *   number + options → select, number (no options) → number
	 */
	inputType?: PersonalizationInputType;
	value?: boolean | string | number;
	defaultValue?: boolean | string | number;
	position?: number;
	hideInPanel?: boolean;
	options?: Array<{ value: boolean | string | number; label: string; description?: string; icon?: string }>;
	description?: string;
	categoryDescription?: string;
	disabled?: boolean;
	icon?: string;
	drilldownDescription?: string;
}

function inferInputType(item: PersonalizationItem): PersonalizationInputType {
	if (item.inputType) return item.inputType;
	if (item.valueType === 'boolean') return 'toggle';
	if (item.valueType === 'string') return item.options?.length ? 'select' : 'text';
	return item.options?.length ? 'select' : 'number';
}

export function itemToSetting(item: PersonalizationItem, onChange: (value: boolean | string | number) => void, allKeys: Set<string>): PersonalizationSettingType {
	const lastDot = item.key.lastIndexOf('.');
	const parentKey = lastDot !== -1 ? item.key.substring(0, lastDot) : undefined;

	let isDrilldownParent = false;
	for (const k of allKeys) {
		if (k.startsWith(item.key + '.')) {
			isDrilldownParent = true;
			break;
		}
	}

	const base = {
		id: item.key,
		category: item.category ?? '',
		categoryDescription: item.categoryDescription,
		label: item.title,
		description: item.description,
		disabled: item.disabled,
		parent: parentKey,
		icon: item.icon,
		drilldownDescription: item.drilldownDescription,
	};
	if (isDrilldownParent) {
		return { ...base, type: 'drilldown' as const, value: null, defaultValue: null, onChange: () => {} };
	}
	if (parentKey !== undefined && !allKeys.has(parentKey)) {
		console.warn(`[PersonalizationService] '${item.key}' has parent '${parentKey}' which is not registered. Register the parent first.`);
	}
	return {
		...base,
		type: inferInputType(item),
		value: item.value ?? item.defaultValue,
		defaultValue: item.defaultValue,
		options: item.options,
		onChange,
	} as PersonalizationSettingType;
}
